// ================================================================
// Costs — gemeinsame Frontend-Bruecke fuer Belege, Fahrten und Fahrzeuge.
// ================================================================
// Alle /api/costs-Pfade leben ausschliesslich hier. Desktop, Mobile und
// kuenftige Projekt-Integrationen importieren diese Funktionen, damit eine
// Aenderung am Backend-Vertrag genau eine Datei betrifft.

import { apiRequest, readErrorMessage } from './apiClient.js';
import { getToken, tryRefresh } from './auth.js';
import { buildCostQuery } from './costScope.ts';
import { ABACUS_ENABLED } from './features.js';

const BASE = '/api/costs';

/** One key per local capture draft. Callers keep it in component state and
 * reuse it for every retry until that draft is either saved or discarded. */
export function newCostIdempotencyKey(scope = 'cost') {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') return `${scope}:${cryptoApi.randomUUID()}`;
  if (typeof cryptoApi?.getRandomValues !== 'function') throw new Error('Secure random UUID generation is unavailable');
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${scope}:${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function queryString(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === 'all') return;
    params.set(key, String(value));
  });
  const query = params.toString();
  return query ? '?' + query : '';
}

export function listCosts(filters = {}) {
  return apiRequest(BASE + buildCostQuery(filters));
}

export function getCostSummary(filters = {}) {
  return apiRequest(BASE + '/summary' + buildCostQuery(filters, false));
}

export function getCostAnalytics(filters = {}) {
  return apiRequest(BASE + '/analytics' + buildCostQuery(filters, false));
}

export function getCostCurrencyPreference() {
  return apiRequest(BASE + '/preferences');
}

export function saveCostCurrencyPreference(currency) {
  return apiRequest(BASE + '/preferences', {
    method: 'PUT',
    body: JSON.stringify({ currency }),
  });
}

export function getCost(id, options = {}) {
  return apiRequest(BASE + '/' + encodeURIComponent(id), options);
}

const TERMINAL_SYNC_STATUSES = new Set(['SYNCED', 'ERROR']);

function costSyncStatus(cost) {
  return String(cost?.bexio?.status || '').toUpperCase();
}

function abortError() {
  const error = new Error('Request aborted');
  error.name = 'AbortError';
  return error;
}

function wait(delayMs, signal) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(abortError()); return; }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Polls one queued Bexio export until the backend reports a terminal state.
 * The deadline also aborts an in-flight fetch, so a network stall cannot keep
 * the UI in SYNCING forever. Repeated reads bypass browser and proxy caches.
 */
export async function waitForCostSync(id, options = {}) {
  const {
    signal,
    onUpdate,
    initialDelayMs = 500,
    maxDelayMs = 4_000,
    timeoutMs = 30_000,
  } = options;
  if (signal?.aborted) throw abortError();

  const controller = new AbortController();
  let deadlineReached = false;
  const abortFromCaller = () => controller.abort();
  signal?.addEventListener('abort', abortFromCaller, { once: true });
  const deadline = setTimeout(() => {
    deadlineReached = true;
    controller.abort();
  }, timeoutMs);

  let delayMs = initialDelayMs;
  try {
    while (true) {
      await wait(delayMs, controller.signal);
      const response = await getCost(id, { signal: controller.signal, cache: 'no-store' });
      const cost = response?.cost;
      if (!cost) throw new Error('Cost sync status response is missing the cost');
      onUpdate?.(cost);
      if (TERMINAL_SYNC_STATUSES.has(costSyncStatus(cost))) return response;
      delayMs = Math.min(delayMs * 2, maxDelayMs);
    }
  } catch (error) {
    if (deadlineReached) {
      const timeoutError = new Error('Timed out while waiting for cost synchronization');
      timeoutError.name = 'CostSyncTimeoutError';
      timeoutError.code = 'COSTS_SYNC_STATUS_TIMEOUT';
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(deadline);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}

/** Evidence-Dateien sind auth-geschuetzt; ein nacktes <img src> kann den
 * Bearer-Header nicht mitsenden. Die UI erzeugt aus diesem Blob eine lokale
 * Object-URL und widerruft sie beim Unmount wieder. */
export async function getCostEvidenceBlob(costId, evidenceId, retried = false) {
  const token = getToken();
  const response = await fetch(
    BASE + '/' + encodeURIComponent(costId) + '/evidence/' + encodeURIComponent(evidenceId),
    { headers: token ? { Authorization: 'Bearer ' + token } : {} },
  );
  if (!response.ok) {
    if (response.status === 401 && !retried && await tryRefresh()) {
      return getCostEvidenceBlob(costId, evidenceId, true);
    }
    let body = {};
    try { body = await response.json(); } catch { /* keine JSON-Antwort */ }
    throw new Error(readErrorMessage(body, response.status));
  }
  return response.blob();
}

export function analyzeExpense(image, fileName, capturedAt, signal) {
  return apiRequest(BASE + '/expenses/analyze', { method: 'POST', signal,
    body: JSON.stringify({ ...(fileName === undefined ? { image } : { file: { dataUrl: image, name: fileName } }), capturedAt }) });
}

export function analyzeReceipt(image, fileName) {
  return apiRequest(BASE + '/receipts/analyze', {
    method: 'POST',
    body: JSON.stringify(fileName === undefined ? { image } : { file: { dataUrl: image, name: fileName } }),
  });
}

export function createReceiptCost(payload) {
  return apiRequest(BASE + '/receipts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function analyzeMileage(image) {
  return apiRequest(BASE + '/mileage/analyze', {
    method: 'POST',
    body: JSON.stringify({ image }),
  });
}

export function getActiveTravel() {
  return apiRequest(BASE + '/travel/active');
}

export function startTravel(payload) {
  return apiRequest(BASE + '/travel/start', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function finishTravel(id, payload) {
  return apiRequest(BASE + '/travel/' + encodeURIComponent(id) + '/finish', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function syncCost(id, options = {}) {
  const { mode, ...requestOptions } = options;
  return apiRequest(BASE + '/' + encodeURIComponent(id) + '/sync', {
    ...requestOptions,
    method: 'POST',
    ...(mode ? { body: JSON.stringify({ mode }) } : {}),
  });
}

export function setCostStatus(id, status) {
  return apiRequest(BASE + '/' + encodeURIComponent(id) + '/status', {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

// Korrigieren / löschen (backend costs.manage.ts). Antwort: { cost, changed, reviewReset }.
export function updateCost(id, changes) {
  return apiRequest(BASE + '/' + encodeURIComponent(id), {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
}

export function deleteCost(id) {
  return apiRequest(BASE + '/' + encodeURIComponent(id), { method: 'DELETE' });
}

export function setCostFxSnapshot(id, payload, options = {}) {
  return apiRequest(BASE + '/' + encodeURIComponent(id) + '/fx', {
    ...options,
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function listVehicles() {
  return apiRequest(BASE + '/vehicles');
}

export function createVehicle(payload) {
  return apiRequest(BASE + '/vehicles', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateVehicle(id, payload) {
  return apiRequest(BASE + '/vehicles/' + encodeURIComponent(id), {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function deleteVehicle(id) {
  return apiRequest(BASE + '/vehicles/' + encodeURIComponent(id), { method: 'DELETE' });
}

/**
 * Katalogstufen: years | makes | models | options | vehicle.
 * Der Backend-Router kann die Datenquelle spaeter wechseln, ohne die UI zu
 * beruehren; `kind` bleibt die semantische Stufe des Auswahlflusses.
 */
export function getVehicleCatalogue(kind, params = {}) {
  const safeKind = String(kind).replace(/[^a-z-]/gi, '');
  return apiRequest(BASE + '/vehicles/catalogue/' + safeKind + queryString(params));
}

export function listFuelCountries() {
  return apiRequest(BASE + '/vehicles/fuel/countries');
}

export function saveBexioConnection(accessToken) {
  return apiRequest(BASE + '/bexio', {
    method: 'PUT',
    body: JSON.stringify({ accessToken }),
  });
}

export function deleteBexioConnection() {
  return apiRequest(BASE + '/bexio', { method: 'DELETE' });
}

export async function getAccountingConnections() {
  const status = await apiRequest(BASE + '/accounting-connections');
  if (ABACUS_ENABLED) return status;
  // Also hide paused controls when a cached/older server still returns Abacus.
  return {
    ...status,
    activeProvider: status.activeProvider === 'ABACUS' ? null : status.activeProvider,
    connections: { bexio: status.connections?.bexio },
  };
}

export function saveAccountingDestination(provider) {
  return apiRequest(BASE + '/accounting-destination', {
    method: 'PUT',
    body: JSON.stringify({ provider }),
  });
}

export function saveAbacusConnection(configuration) {
  return apiRequest(BASE + '/abacus', {
    method: 'PUT',
    body: JSON.stringify(configuration),
  });
}

export function deleteAbacusConnection() {
  return apiRequest(BASE + '/abacus', { method: 'DELETE' });
}

export function getBexioAccountingMapping({ signal } = {}) {
  return apiRequest(BASE + '/bexio/accounting-mapping', { signal });
}

export function getBexioAccountingCatalogue() {
  return apiRequest(BASE + '/bexio/accounting-catalogue');
}

export function saveBexioAccountingMapping(mapping) {
  return apiRequest(BASE + '/bexio/accounting-mapping', {
    method: 'PUT',
    body: JSON.stringify(mapping),
  });
}

export function getAbacusAccountingMapping() {
  return apiRequest(BASE + '/abacus/accounting-mapping');
}

export function getAbacusAccountingCatalogue() {
  return apiRequest(BASE + '/abacus/accounting-catalogue');
}

export function saveAbacusAccountingMapping(mapping) {
  return apiRequest(BASE + '/abacus/accounting-mapping', {
    method: 'PUT',
    body: JSON.stringify(mapping),
  });
}
